import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { X, Save, Pencil, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/** Per-section field definitions. Each field: {key, label, type, required?, rows?}
 * type: "text" | "textarea" | "url" | "datetime" | "number" */
const SCHEMAS = {
  news: {
    label: "News Article",
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "news_site", label: "Source / Site", type: "text" },
      { key: "url", label: "URL", type: "url", required: true },
      { key: "image_url", label: "Image URL", type: "url" },
      { key: "published_at", label: "Published (ISO datetime)", type: "datetime", required: true },
      { key: "summary", label: "Summary", type: "textarea", rows: 4 },
    ],
  },
  rocket: {
    label: "Rocket",
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "full_name", label: "Full Name", type: "text" },
      { key: "family", label: "Family", type: "text" },
      { key: "variant", label: "Variant", type: "text" },
      { key: "manufacturer_name", label: "Manufacturer", type: "text" },
      { key: "length", label: "Length (m)", type: "number" },
      { key: "diameter", label: "Diameter (m)", type: "number" },
      { key: "launch_mass", label: "Launch Mass (t)", type: "number" },
      { key: "thrust", label: "Thrust (kN)", type: "number" },
      { key: "image_url", label: "Image URL", type: "url" },
      { key: "wiki_url", label: "Wikipedia URL", type: "url" },
      { key: "info_url", label: "Official URL", type: "url" },
      { key: "description", label: "Description", type: "textarea", rows: 4 },
    ],
  },
  launch: {
    label: "Launch",
    fields: [
      { key: "name", label: "Mission Name", type: "text", required: true },
      { key: "net", label: "NET (ISO datetime)", type: "datetime", required: true },
      { key: "provider", label: "Launch Provider", type: "text" },
      { key: "rocket_name", label: "Rocket", type: "text" },
      { key: "status_name", label: "Status", type: "text" },
      { key: "status_abbrev", label: "Status Abbrev (e.g. Go, TBD, Success)", type: "text" },
      { key: "pad_name", label: "Pad Name", type: "text" },
      { key: "pad_location", label: "Pad Location", type: "text" },
      { key: "mission_type", label: "Mission Type", type: "text" },
      { key: "orbit_name", label: "Target Orbit", type: "text" },
      { key: "image_url", label: "Image URL", type: "url" },
      { key: "mission_description", label: "Mission Description", type: "textarea", rows: 4 },
    ],
  },
  event: {
    label: "Event",
    fields: [
      { key: "name", label: "Event Name", type: "text", required: true },
      { key: "date", label: "Date (ISO datetime)", type: "datetime", required: true },
      { key: "location", label: "Location", type: "text" },
      { key: "event_type", label: "Type (e.g. Docking, Landing, Launch)", type: "text" },
      { key: "image_url", label: "Feature Image URL", type: "url" },
      { key: "description", label: "Description", type: "textarea", rows: 4 },
    ],
  },
};

/**
 * AdminContentForm — modal form for creating/editing admin content in any of
 * the 4 sections (news, rocket, launch, event). Portal-rendered.
 *
 * Props:
 *   section:  "news" | "rocket" | "launch" | "event"
 *   initial:  existing admin_content doc's `data` (+ `id` for edit) — omit for create
 *   initialId: string (required for edit)
 *   onClose:  fn
 *   onSaved:  fn — called after successful save
 */
export default function AdminContentForm({ section, initial, initialId, onClose, onSaved }) {
  const schema = SCHEMAS[section];
  const editing = !!initialId;

  const [form, setForm] = useState(() => {
    const seed = {};
    for (const f of schema.fields) seed[f.key] = (initial && initial[f.key]) || "";
    return seed;
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    // Coerce numbers
    const data = { ...form };
    for (const f of schema.fields) {
      if (f.type === "number" && data[f.key] !== "" && data[f.key] != null) {
        const n = parseFloat(data[f.key]);
        data[f.key] = Number.isFinite(n) ? n : null;
      }
      if (data[f.key] === "") data[f.key] = null;
    }
    setSaving(true);
    try {
      const payload = { section, data };
      if (editing) {
        await api.put(`/admin/content/${initialId}`, payload);
        toast.success(`${schema.label} updated`);
      } else {
        await api.post("/admin/content", payload);
        toast.success(`${schema.label} created`);
      }
      onSaved && onSaved();
    } catch (err) {
      toast.error("Save failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      data-testid="admin-form-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form onSubmit={submit}
            className="sat-brackets sat-surface border border-[color:var(--sat-cyan)] p-5 md:p-6 w-full max-w-2xl my-8 sat-box-glow"
            data-testid={`admin-form-${section}`}>
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-[color:var(--sat-border-bright)]">
          <div>
            <div className="sat-label sat-cyan">DIRECTIVE // {editing ? "AMEND" : "CREATE"} · {section.toUpperCase()}</div>
            <h2 className="font-display font-bold text-xl mt-1">
              {editing ? `Edit ${schema.label}` : `New ${schema.label}`}
            </h2>
          </div>
          <button type="button" onClick={onClose}
                  className="text-[color:var(--sat-text-dim)] hover:text-[color:var(--sat-cyan)]"
                  data-testid="admin-form-close" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {schema.fields.map(f => (
            <label key={f.key} className={f.type === "textarea" ? "col-span-1 md:col-span-2" : ""}>
              <div className="sat-label mb-1">{f.label}{f.required && " *"}</div>
              {f.type === "textarea" ? (
                <textarea required={f.required} rows={f.rows || 3}
                          value={form[f.key] || ""} onChange={set(f.key)}
                          data-testid={`admin-field-${section}-${f.key}`} />
              ) : (
                <input required={f.required}
                       type={f.type === "number" ? "number" : f.type === "url" ? "url" : "text"}
                       step={f.type === "number" ? "any" : undefined}
                       placeholder={f.type === "datetime" ? "2026-12-31T20:00:00Z" : ""}
                       value={form[f.key] || ""} onChange={set(f.key)}
                       data-testid={`admin-field-${section}-${f.key}`} />
              )}
            </label>
          ))}
        </div>

        <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-[color:var(--sat-border-bright)]">
          <button type="button" onClick={onClose}
                  className="sat-btn" data-testid="admin-form-cancel">CANCEL</button>
          <button type="submit" disabled={saving}
                  className="sat-btn sat-btn-solid disabled:opacity-50"
                  data-testid="admin-form-save">
            <Save size={13} /> {saving ? "SAVING…" : (editing ? "UPDATE" : "CREATE")}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}

/** unshape — convert a "shaped" (upstream-compatible) admin item back into
 * the flat data{} object that the form expects. Inverse of backend
 * `_admin_shape`. */
export function unshapeForEdit(section, shaped) {
  if (!shaped) return {};
  if (section === "news") {
    return {
      title: shaped.title, url: shaped.url, image_url: shaped.image_url,
      news_site: shaped.news_site, summary: shaped.summary, published_at: shaped.published_at,
    };
  }
  if (section === "rocket") {
    return {
      name: shaped.name, full_name: shaped.full_name, family: shaped.family, variant: shaped.variant,
      manufacturer_name: shaped.manufacturer?.name || "",
      length: shaped.length, diameter: shaped.diameter, launch_mass: shaped.launch_mass,
      thrust: shaped.to_thrust, image_url: shaped.image_url,
      wiki_url: shaped.wiki_url, info_url: shaped.info_url,
      description: shaped.description,
    };
  }
  if (section === "launch") {
    return {
      name: shaped.name, net: shaped.net,
      provider: shaped.launch_service_provider?.name || "",
      rocket_name: shaped.rocket?.configuration?.name || "",
      status_name: shaped.status?.name || "",
      status_abbrev: shaped.status?.abbrev || "",
      pad_name: shaped.pad?.name || "",
      pad_location: shaped.pad?.location?.name || "",
      mission_type: shaped.mission?.type || "",
      orbit_name: shaped.mission?.orbit?.name || "",
      image_url: shaped.image,
      mission_description: shaped.mission?.description || "",
    };
  }
  if (section === "event") {
    return {
      name: shaped.name, date: shaped.date, description: shaped.description,
      location: shaped.location, event_type: shaped.type?.name || "",
      image_url: shaped.feature_image,
    };
  }
  return shaped;
}

/** useAdminSection — encapsulates the create/edit/delete flow for a given
 * admin section. Returns state + handlers to be spread over UI. */
export function useAdminSection(section, reload) {
  const [formOpen, setFormOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null); // {admin_id, ...unshaped}

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (item) => {
    if (!item?.admin_id) return;
    setEditing({ admin_id: item.admin_id, ...unshapeForEdit(section, item) });
    setFormOpen(true);
  };
  const onDelete = async (item) => {
    if (!item?.admin_id) return;
    if (!window.confirm(`Delete "${item.title || item.name}"?`)) return;
    try {
      await api.delete(`/admin/content/${item.admin_id}`);
      toast.success("Deleted");
      reload && reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };
  const closeForm = () => { setFormOpen(false); setEditing(null); };
  const onSaved = () => { closeForm(); reload && reload(); };

  return { formOpen, editing, openNew, openEdit, onDelete, closeForm, onSaved };
}

/** AdminNewButton — small "+ NEW ARTICLE" style button visible only for admin. */
export function AdminNewButton({ section, label, onClick, testid }) {
  return (
    <button onClick={onClick} className="sat-btn sat-btn-solid" data-testid={testid || `admin-new-${section}`}>
      + NEW {label.toUpperCase()}
    </button>
  );
}

/** AdminItemWrap — wraps any card so admin-created items get an "ADMIN" chip
 * (always visible to everyone) plus edit/delete controls (visible to admins).
 * If the item isn't admin-created, renders children unchanged. */
export function AdminItemWrap({ item, isAdmin, onEdit, onDelete, children }) {
  if (!item?.is_admin_created) return <>{children}</>;
  return (
    <div className="relative" data-testid="admin-wrap">
      <div className="absolute top-1 left-1 z-10 flex items-center gap-1 pointer-events-auto">
        <span className="sat-label border border-[color:var(--sat-cyan)] bg-black/70 sat-cyan px-1.5 py-0.5"
              data-testid="admin-badge">ADMIN</span>
        {isAdmin && (
          <>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit && onEdit(item); }}
              className="p-1 border border-[color:var(--sat-cyan)] bg-black/70 sat-cyan hover:bg-[color:var(--sat-cyan-soft)]"
              data-testid="admin-edit-btn" aria-label="Edit"
              title="Edit"
            ><Pencil size={11} /></button>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete && onDelete(item); }}
              className="p-1 border border-[color:var(--sat-live)] bg-black/70 text-[color:var(--sat-live)] hover:bg-[color:var(--sat-live)]/20"
              data-testid="admin-delete-btn" aria-label="Delete"
              title="Delete"
            ><Trash2 size={11} /></button>
          </>
        )}
      </div>
      {children}
    </div>
  );
}
